import samlTemplateUrl from '../../../app/saml/saml.tpl.html';
import { memoize } from 'lodash';
import logger from '../../../common/logger';

const log = logger('SAML');
const error = log.extend('error');

const GENERAL_SAML_DISPLAY_MSG = 'Error processing SAML token. Please contact your administrator.';
// Must match UserServlet.CRBAC_NON_SPLUNK_IDP_ERROR_TYPE.
const CRBAC_NON_SPLUNK_IDP_ERROR_TYPE = 'CRBAC_NON_SPLUNK_IDP';
const CRBAC_NON_SPLUNK_IDP_DISPLAY_MSG =
    'Your administrator has enabled Unified Login with Splunk Cloud for logging in to Splunk Observability Cloud. Please use your Splunk Cloud credentials to sign in. If issues continue to arise, please reach out to your administrator.';

function parseBrokerTarget(target) {
    if (typeof target !== 'string' || !target.startsWith('/') || target.startsWith('//')) {
        return { isBrokerTarget: false, tx: null };
    }

    try {
        // `.invalid` is a reserved TLD; this base is only used to parse a relative target.
        const parsed = new URL(target, 'https://signalview.invalid');
        if (parsed.pathname !== '/oauth-authorize') {
            return { isBrokerTarget: false, tx: null };
        }

        const parameters = Array.from(parsed.searchParams.entries());
        if (parsed.hash || parameters.length !== 1) {
            return { isBrokerTarget: true, tx: null };
        }

        const [key, tx] = parameters[0];
        if (key !== 'tx' || !tx?.trim()) {
            return { isBrokerTarget: true, tx: null };
        }

        return { isBrokerTarget: true, tx };
    } catch (_) {
        return { isBrokerTarget: false, tx: null };
    }
}

/**
 * Validates and Exchanges a SAML token for a user token
 */
function validateAndExchangeToken(token, { security, signalboost, logService }) {
    function respond() {
        if (!token) {
            log('No saml token.');
            return Promise.reject(null);
        } else {
            logService.logData(
                'INFO',
                'saml.js -> validateAndExchangeToken: exchanging SAML token'
            );
            return signalboost.user.exchangeSaml(token);
        }
    }
    logService.logData(
        'INFO',
        'saml.js -> validateAndExchangeToken: logging out and clearing the token'
    );

    // remove current auth token, regardless of saml token validity
    return security.logout().then(respond, respond);
}

/**
 * ## Exchanges a SAML token for a User token (once only)
 * Token exchange is followed by redirecting user to correct location.
 *
 * Successive call with same SAML token will return the
 * response of very first call.
 */
const processSamlTokenOnce = memoize(function (
    token,
    {
        $rootScope,
        $window,
        $location,
        security,
        signalboost,
        loginService,
        ssoRedirect,
        logService,
        auth,
        currentUser,
        oauthBrokerContinuation,
    }
) {
    const split = token.split(':::');
    let url = '';
    let actualToken = token;
    if (split !== undefined && split.length > 1) {
        actualToken = split[0];
        url = split[1].replace(/-/g, '+').replace(/_/g, '/').replace(/=/g, '');
    }

    const brokerInspection = oauthBrokerContinuation.inspect();
    let target = '';
    let brokerTarget = { isBrokerTarget: false, tx: null };
    let targetDecodeFailed = false;
    if (url) {
        try {
            target = atob(url);
            brokerTarget = parseBrokerTarget(target);
        } catch (_) {
            targetDecodeFailed = true;
        }
    }

    const brokerTargetMismatch =
        brokerInspection.owned &&
        (!brokerInspection.active ||
            !url ||
            targetDecodeFailed ||
            !brokerTarget.isBrokerTarget ||
            brokerTarget.tx !== oauthBrokerContinuation.getTx());
    if (brokerTargetMismatch || (!brokerInspection.owned && brokerTarget.isBrokerTarget)) {
        oauthBrokerContinuation.setTerminal('state_mismatch');
        $location.url('/oauth-authorize');
        return Promise.resolve();
    }

    return validateAndExchangeToken(actualToken, { security, signalboost, logService })
        .then(function (result) {
            if (result && result.redirectUrl) {
                if (brokerInspection.owned) {
                    oauthBrokerContinuation.setTerminal('malformed_response');
                    $location.url('/oauth-authorize');
                    return;
                }
                logService.logData(
                    'INFO',
                    'saml.js -> validateAndExchangeToken: received token from api'
                );
                $window.location = result.redirectUrl;
            } else {
                const sessionToken = result && (result.accessToken || result.sf_accessToken);
                if (sessionToken && auth.authToken() !== sessionToken) {
                    auth.setAuth(sessionToken);
                }
                currentUser.clearUserCache();
                return loginService.initOrgAndUserPostLogin().then(function ({ user }) {
                    $rootScope.$broadcast('current user changed', user);
                    if (brokerInspection.owned) {
                        $location.url('/oauth-authorize');
                    } else if (!!url) {
                        if (targetDecodeFailed) {
                            throw new Error('Invalid SAML target');
                        }
                        $window.location = target;
                    } else {
                        url = ssoRedirect.getAndClear();
                        logService.logData(
                            'INFO',
                            'saml.js -> validateAndExchangeToken -> initOrgAndUserPostLogin: checked SSO redirect'
                        );
                        if (!!url) {
                            logService.logData(
                                'INFO',
                                'saml.js -> validateAndExchangeToken -> initOrgAndUserPostLogin: applying SSO redirect'
                            );
                            $location.url(url);
                        } else {
                            $location.path('/home');
                        }
                    }
                });
            }
        })
        .catch(function (e) {
            if (brokerInspection.owned) {
                oauthBrokerContinuation.setTerminal('unknown');
                $location.url('/oauth-authorize');
                return;
            }

            logService.logData(
                'ERROR',
                'saml.js -> validateAndExchangeToken -> initOrgAndUserPostLogin: error while processing token'
            );

            const serverErrorType = e && e.data && e.data.errorType;
            const alertMsg =
                serverErrorType === CRBAC_NON_SPLUNK_IDP_ERROR_TYPE
                    ? CRBAC_NON_SPLUNK_IDP_DISPLAY_MSG
                    : GENERAL_SAML_DISPLAY_MSG;
            $window.alert(alertMsg);
            error('Error exchanging saml token.');
            $location.path('/signin');
        });
});

const SamlTokenController = [
    '$rootScope',
    '$window',
    '$location',
    '$scope',
    'security',
    'signalboost',
    'loginService',
    'ssoRedirect',
    'logService',
    'auth',
    'currentUser',
    'oauthBrokerContinuation',
    function (
        $rootScope,
        $window,
        $location,
        $scope,
        security,
        signalboost,
        loginService,
        ssoRedirect,
        logService,
        auth,
        currentUser,
        oauthBrokerContinuation
    ) {
        const $ctrl = this;

        $scope.processSamlToken = (token) =>
            processSamlTokenOnce(token, {
                $rootScope,
                $window,
                $location,
                security,
                signalboost,
                loginService,
                ssoRedirect,
                logService,
                auth,
                currentUser,
                oauthBrokerContinuation,
            });

        $ctrl.$onInit = () => {
            $scope.processSamlToken($ctrl.samlToken).finally(() => {
                $scope.$applyAsync();
            });
        };
    },
];

// there is no ui for the saml token exchange
// it's just a redirect to user's homepage
// with an intermediate step of exchanging
// the saml token for an access token
export default {
    bindings: { samlToken: '<' },
    templateUrl: samlTemplateUrl,
    controller: SamlTokenController,
};
